import React, { ForwardedRef } from "react";
import MuiTextField, { TextFieldProps } from "@mui/material/TextField";
import clsx from "clsx";

import {
  useInputStyles,
  useInputLabelStyles,
  useStyles,
} from "./TextFieldBase.styles";
import { TextFieldBaseProps } from "./TextFieldBase.types";
import useId from "../hooks/useId";
import useRandom from "../hooks/useRandom";
import { useFormHelperTextStyles } from "../FormHelperText/FormHelperText.styles";
import { Tooltip } from "..";

// ":-internal-autofill-previewed" is the one that catches Chrome's pre-gesture
// preview; the others cover committed autofill and the standardised alias.
const AUTO_FILL_SELECTORS = [
  ":-internal-autofill-previewed",
  ":-internal-autofill-selected",
  ":-webkit-autofill",
  ":autofill",
];

const AUTO_FILL_POLL_DELAYS = [120, 350, 800];

function TextFieldBase(
  props: TextFieldBaseProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    autoComplete: autoCompleteProp,
    className: classNameProp,
    hiddenLabel,
    fullWidth = true,
    id: idProp,
    InputLabelProps: InputLabelPropsProp,
    InputProps: InputPropsProp,
    FormHelperTextProps: FormHelperTextPropsProp,
    helperText,
    showHelperTextMargin,
    disabled,
    disabledTooltip,
    tooltipAction,
    ...other
  } = props;

  const classes = useStyles();

  const id = useId(idProp);

  const autoCompleteRandom = useRandom();

  const inputLabelClasses = useInputLabelStyles(InputLabelPropsProp);

  const inputClasses = useInputStyles(InputPropsProp);

  const formHelperTextClasses = useFormHelperTextStyles(
    FormHelperTextPropsProp
  );

  // Chrome's autofill "preview" paints the value but hides it from JS and fires no
  // events until the user interacts. It matches :-internal-autofill-previewed, not
  // the :-webkit-autofill that MUI's own detection hooks, so MUI leaves the label
  // unshrunk on top of the painted text until the first click.
  const currentValue = (other as { value?: unknown }).value;

  const [autoFilled, setAutoFilled] = React.useState(false);

  React.useEffect(() => {
    const isAutoFilled = () => {
      const node = document.getElementById(id);
      if (!node) {
        return false;
      }
      return AUTO_FILL_SELECTORS.some((selector) => {
        try {
          return node.matches(selector);
        } catch {
          // Browsers that don't know a selector throw on matches().
          return false;
        }
      });
    };

    if (isAutoFilled()) {
      setAutoFilled(true);
      return undefined;
    }

    // Chrome can apply the preview after mount, with timing that varies by how long
    // the bundle takes to parse, so re-check a few times rather than once.
    const timers = AUTO_FILL_POLL_DELAYS.map((delay) =>
      window.setTimeout(() => {
        if (isAutoFilled()) {
          setAutoFilled(true);
        }
      }, delay)
    );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [id]);

  // Once a real value arrives, hand control back to MUI.
  React.useEffect(() => {
    if (currentValue) {
      setAutoFilled(false);
    }
  }, [currentValue]);

  const InputLabelProps = React.useMemo(() => {
    const forceShrink =
      autoFilled && !currentValue && InputLabelPropsProp?.shrink === undefined;
    return {
      ...InputLabelPropsProp,
      classes: inputLabelClasses,
      ...(forceShrink ? { shrink: true } : {}),
    };
  }, [InputLabelPropsProp, inputLabelClasses, autoFilled, currentValue]);

  const InputProps = React.useMemo(() => {
    return {
      ...InputPropsProp,
      classes: inputClasses,
    };
  }, [InputPropsProp, inputClasses]);

  const FormHelperTextProps = React.useMemo(() => {
    return {
      ...FormHelperTextPropsProp,
      classes: formHelperTextClasses,
    };
  }, [FormHelperTextPropsProp, formHelperTextClasses]);

  const autoComplete = autoCompleteProp || autoCompleteRandom;

  const className = clsx(classNameProp, {
    [classes.hiddenLabel]: hiddenLabel,
    [classes.helperTextMargin]: !helperText && showHelperTextMargin,
  });

  const textField = (
    <InternalTextField
      {...other}
      autoComplete={autoComplete}
      className={className}
      fullWidth={fullWidth}
      InputLabelProps={InputLabelProps}
      InputProps={InputProps}
      id={id}
      ref={ref}
      variant="outlined"
      helperText={helperText}
      FormHelperTextProps={FormHelperTextProps}
      disabled={disabled}
    />
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip} action={tooltipAction}>
        {textField}
      </Tooltip>
    );
  }

  return textField;
}

const InternalTextField = React.forwardRef(function (
  props: TextFieldProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    inputProps: inputPropsProp,
    onKeyDownCapture: onKeyDownCaptureProp,
    onFocusCapture: onFocusCaptureProp,
    onFocus,
    disabled,
  } = props;

  const onKeyDownCapture = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) {
      if (event.key !== "Enter" && event.key !== "Tab") {
        event.preventDefault();
      }
      event.stopPropagation();
      return;
    }
    onKeyDownCaptureProp?.(event);
  };

  const handleFocusCapture = (event: React.FocusEvent<HTMLInputElement>) => {
    if (disabled) {
      onFocus?.(event);
    } else {
      onFocusCaptureProp?.(event);
    }
  };

  return (
    <MuiTextField
      {...props}
      ref={ref}
      disabled={disabled}
      onKeyDownCapture={onKeyDownCapture}
      inputProps={{
        ...inputPropsProp,
        onFocusCapture: handleFocusCapture,
        disabled: false,
        "aria-disabled": disabled,
      }}
    />
  );
});

export default React.forwardRef(TextFieldBase);
